/**
 * Commodity Intelligence Platform
 * Main Application Component
 *
 * Nigerian Market Analytics Platform - 4-State Pilot Implementation
 * Built with React.js and Material UI
 *
 * Routes:
 * - Public site: /, /commodities, /markets, /insights (PublicLayout)
 * - /login: authentication gate
 * - /app/*: Premium user workspace (analytics + personalization) - role=premium
 * - /admin/*: Admin workspace (administration only) - role=admin
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeContextProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Layout } from './components/layout';
import PublicLayout from './components/public/PublicLayout';

// Public page imports
import Home from './pages/public/Home';
import PublicCommodities from './pages/public/PublicCommodities';
import PublicMarkets from './pages/public/PublicMarkets';
import PublicInsights from './pages/public/PublicInsights';
import Developers from './pages/public/Developers';

// Auth
import Login from './pages/auth/Login';
import ChoosePassword from './pages/auth/ChoosePassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import Signup from './pages/auth/Signup';
import VerifyEmail from './pages/auth/VerifyEmail';

// Premium (analytics) pages - formerly /admin/*, now /app/*
import Dashboard from './pages/dashboard/Dashboard';
import GlobalCommodities from './pages/global-commodities/GlobalCommodities';
import Markets from './pages/markets/Markets';
import CurrencyAnalysis from './pages/currency/CurrencyAnalysis';

// Premium (personalization) pages - new
import Alerts from './pages/premium/Alerts';
import Portfolio from './pages/premium/Portfolio';
import Subscription from './pages/premium/Subscription';
import ApiKeys from './pages/premium/ApiKeys';

// Account pages - any signed-in role
import Profile from './pages/premium/Profile';
import Settings from './pages/premium/Settings';

// Admin (administration only) pages
import {
  CommodityManagement, MarketManagement, DataUpload, UploadHistory, AliasManagement,
  UserManagement, ApiConfig,
} from './pages/admin';

// Wrapper helpers
const Premium = ({ children }) => (
  <ProtectedRoute role="premium"><Layout>{children}</Layout></ProtectedRoute>
);
const Admin = ({ children }) => (
  <ProtectedRoute role="admin"><Layout>{children}</Layout></ProtectedRoute>
);
// Any signed-in role (Profile / Settings live in the avatar menu for both)
const Authed = ({ children }) => (
  <ProtectedRoute><Layout>{children}</Layout></ProtectedRoute>
);

// Route changes land at the top of the target page; without this the browser
// keeps the previous page's scroll position on client-side navigations.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Single React Query client for the app - powers API data fetching/caching as
// pages migrate off mockData.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 60_000 },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeContextProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* ===== PUBLIC ROUTES ===== */}
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/commodities" element={<PublicLayout><PublicCommodities /></PublicLayout>} />
            <Route path="/markets" element={<PublicLayout><PublicMarkets /></PublicLayout>} />
            <Route path="/insights" element={<PublicLayout><PublicInsights /></PublicLayout>} />
            <Route path="/developers" element={<PublicLayout><Developers /></PublicLayout>} />

            {/* ===== AUTH ===== */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<ChoosePassword mode="invite" />} />
            <Route path="/reset-password" element={<ChoosePassword mode="reset" />} />

            {/* ===== PREMIUM WORKSPACE (/app/*) ===== */}
            <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/app/dashboard" element={<Premium><Dashboard /></Premium>} />
            <Route path="/app/global-commodities" element={<Premium><GlobalCommodities /></Premium>} />
            <Route path="/app/markets" element={<Premium><Markets /></Premium>} />
            {/* Backwards-compat redirects: old separate Local Markets / Market Map paths land on the merged Markets page with the right tab. */}
            <Route path="/app/local-markets" element={<Navigate to="/app/markets?tab=records" replace />} />
            <Route path="/app/market-map" element={<Navigate to="/app/markets?tab=map" replace />} />
            <Route path="/app/currency" element={<Premium><CurrencyAnalysis /></Premium>} />
            <Route path="/app/alerts" element={<Premium><Alerts /></Premium>} />
            <Route path="/app/portfolio" element={<Premium><Portfolio /></Premium>} />
            <Route path="/app/subscription" element={<Premium><Subscription /></Premium>} />
            <Route path="/app/api-keys" element={<Authed><ApiKeys /></Authed>} />
            <Route path="/app/profile" element={<Authed><Profile /></Authed>} />
            <Route path="/app/settings" element={<Authed><Settings /></Authed>} />

            {/* ===== ADMIN WORKSPACE (/admin/*) ===== */}
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/users" element={<Admin><UserManagement /></Admin>} />
            <Route path="/admin/api-config" element={<Admin><ApiConfig /></Admin>} />
            <Route path="/admin/commodities" element={<Admin><CommodityManagement /></Admin>} />
            <Route path="/admin/markets" element={<Admin><MarketManagement /></Admin>} />
            <Route path="/admin/upload" element={<Admin><DataUpload /></Admin>} />
            <Route path="/admin/upload/history" element={<Admin><UploadHistory /></Admin>} />
            <Route path="/admin/aliases" element={<Admin><AliasManagement /></Admin>} />

            {/* Catch all → home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeContextProvider>
    </QueryClientProvider>
  );
}

export default App;
